import React from "react";
import { Typography } from "@mui/material";
import styled from "styled-components";
import { ReactComponent as WebsiteLinkIcon } from "../LinkList/website-link.svg";
import { ViewDiff, isDiffEmptyForVariant } from "../ViewDiff";
import { CirclePhoto } from "../CirclePhoto";

const displayNames = {
	political: 'Political Background',
	professional: 'Professional Background',
	personal: 'Personal Background',
};

const BIO_KEYS = ['political', 'professional', 'personal'];

function getBioDiffForType(diff, type) {
	if (!diff) return undefined;
	const fromBios = diff.bios?.find((b) => b.type === type);
	if (fromBios != null) return fromBios.diff;
	const key = 'bio' + type.charAt(0).toUpperCase() + type.slice(1);
	const raw = diff[key];
	return Array.isArray(raw) ? raw : undefined;
}

function getPhotoPathFromSegments(segments, variant) {
	if (!Array.isArray(segments)) return '';
	if (variant === 'before') return segments.filter((p) => !p.added).map((p) => p.value || '').join('');
	return segments.filter((p) => !p.removed).map((p) => p.value || '').join('');
}

function getDisplayForUrl(url) {
	if (!url) return '';
	const splitUrl = (url || '').split(/^(https?:\/\/)?(www\.)?([^/]*)?/);
	return splitUrl.length > 1 ? (splitUrl[3] || url) : url;
}

/** Preview block: photo + name + subtitle. */
function ColumnPreview({ photoPath, candidate, race, isBefore }) {
	const name = candidate?.stagingDraft?.name || candidate?.name || '';
	const subtitle = candidate?.name && race
		? `${candidate.name} is running for ${race.officeName} in ${race.district?.longName}.`
		: '';
	return (
		<div>
			<Typography variant="h2" color="textPrimary" mb={1}>{isBefore ? 'Before' : 'After'}</Typography>
			<ColumnPreviewBlock>
				<CirclePhoto size="large" photoPath={photoPath || undefined} />
				<ColumnPreviewText>
					<Typography variant="h2" color="textPrimary">{name}</Typography>
					{subtitle && (
						<Typography variant="body1" color="textPrimary" style={{ marginTop: 8, marginBottom: 8 }}>
							{subtitle}
						</Typography>
					)}
				</ColumnPreviewText>
			</ColumnPreviewBlock>
		</div>
	);
}

/** One reference link: website-style with optional highlight. */
function ReferenceLink({ url, title, highlight }) {
	const display = getDisplayForUrl(url);
	if (!url) return null;
	return (
		<RefLinkRow $highlight={highlight}>
			<WebsiteLinkIcon />
			<a href={url} target="_blank" rel="noopener noreferrer">{display || url}</a>
		</RefLinkRow>
	);
}

/** Row with two aligned cells (before | after). */
function DiffRow({ left, right }) {
	return (
		<Row>
			<RowCell className="before">{left}</RowCell>
			<RowCell className="after">{right}</RowCell>
		</Row>
	);
}

/** Single cell content for a diff row; empty side renders EmptyCell. */
function DiffCell({ diff, variant, label }) {
	const segmentDiff = Array.isArray(diff) ? diff : [];
	const isEmpty = isDiffEmptyForVariant(segmentDiff, variant);
	if (isEmpty) return <EmptyCell />;
	return (
		<ItemCell className={variant === 'after' ? 'after' : ''}>
			{label && <ItemSectionLabel>{label}</ItemSectionLabel>}
			<ViewDiff diff={segmentDiff} variant={variant} />
		</ItemCell>
	);
}

const ViewCandidateDiff = ({ diff, issuesByKey = {}, candidate, race }) => {
	const beforePhotoPath = getPhotoPathFromSegments(diff?.photoPathFace, 'before');
	const afterPhotoPath = getPhotoPathFromSegments(diff?.photoPathFace, 'after');
	const references = Array.isArray(diff?.references) ? diff.references : [];
	const beforeRefs = references.filter((r) => !Boolean(r.added));
	const afterRefs = references.filter((r) => !Boolean(r.removed));

	return (
		<Wrapper>
			{/* Preview row: same horizontal line */}
			<DiffRow
				left={<ColumnPreview photoPath={beforePhotoPath} candidate={candidate} race={race} isBefore={true} />}
				right={<ColumnPreview photoPath={afterPhotoPath} candidate={candidate} race={race} />}
			/>

			<SectionHeaderFull variant="h4" color="textPrimary">References</SectionHeaderFull>
			<DiffRow
				left={
					beforeRefs.length === 0 ? (
						<EmptyCell />
					) : (
						<ReferencesBlock>
							{beforeRefs.map((ref, i) => (
								<ReferenceLink
									key={ref.url ? `${ref.url}-${i}` : `before-ref-${i}`}
									url={ref.url}
									title={ref.title}
									highlight={Boolean(ref.removed) ? 'red' : 'plain'}
								/>
							))}
						</ReferencesBlock>
					)
				}
				right={
					afterRefs.length === 0 ? (
						<EmptyCell />
					) : (
						<ReferencesBlock>
							{afterRefs.map((ref, i) => (
								<ReferenceLink
									key={ref.url ? `${ref.url}-${i}` : `after-ref-${i}`}
									url={ref.url}
									title={ref.title}
									highlight={Boolean(ref.added) ? 'green' : 'plain'}
								/>
							))}
						</ReferencesBlock>
					)
				}
			/>
			<SectionHeaderFull variant="h4" color="textPrimary">Background</SectionHeaderFull>
			{BIO_KEYS.map((key) => {
				const bioDiff = getBioDiffForType(diff, key);
				return (
					<DiffRow
						key={key}
						left={<DiffCell diff={bioDiff} variant="before" label={displayNames[key]} />}
						right={<DiffCell diff={bioDiff} variant="after" label={displayNames[key]} />}
					/>
				);
			})}

			<Seperator />
			<SectionHeaderFull variant="h4" color="textPrimary">Issues</SectionHeaderFull>
			{(diff?.issues || []).map((issue, index) => (
				<IssueSection key={index}>
					<IssueHeader variant="h4" color="textPrimary">
						{issuesByKey[issue.key]?.name ?? issue.key}
					</IssueHeader>
					{(issue.stances || issue.diff || []).length === 0 ? (
						<DiffRow left={<EmptyCell />} right={<EmptyCell />} />
					) : (
						(issue.stances || issue.diff).map((stance) => (
							<DiffRow
								key={stance.stanceId}
								left={<DiffCell diff={stance.diff} variant="before" />}
								right={<DiffCell diff={stance.diff} variant="after" />}
							/>
						))
					)}
				</IssueSection>
			))}
		</Wrapper>
	);
};

const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	gap: 16px;
`;

const Row = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 16px;
	align-items: start;
	min-height: 0;

	@media (max-width: 900px) {
		grid-template-columns: 1fr;
	}
`;

const RowCell = styled.div`
	min-width: 0;
	min-height: 24px;
	&.before { }
	&.after { }
`;

const SectionHeaderFull = styled(Typography)`
	margin-bottom: 4px;
`;

const ColumnPreviewBlock = styled.div`
	display: flex;
	flex-direction: row;
	align-items: flex-start;
	gap: 12px;
`;

const ColumnPreviewText = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	min-width: 0;
`;

const ItemSectionLabel = styled.div`
	font-size: 0.875rem;
	font-weight: 600;
	color: #333;
	margin-bottom: 6px;
`;

const ItemCell = styled.div`
	min-width: 0;
	padding: 12px;
	border: 1px solid #eee;
	border-radius: 6px;
	background-color: #fafafa;
	&.after { background-color: #fff; }
`;

const EmptyCell = styled.div`
	min-width: 0;
	min-height: 24px;
`;

const IssueSection = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	margin-bottom: 28px;
	&:last-child {
		margin-bottom: 0;
	}
`;

const IssueHeader = styled(Typography)`
	margin-bottom: 6px;
	font-weight: 600;
`;

const Seperator = styled.div`
	height: 1px;
	width: 100%;
	background-color: #e0e0e0;
	margin: 4px 0;
`;

const ReferencesBlock = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

const RefLinkRow = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 4px;
	padding: 6px 8px;
	border-radius: 4px;
	& > svg { flex-shrink: 0; }
	& > a {
		color: #22034f;
		opacity: 0.7;
		font-size: 14px;
		overflow: hidden;
		text-overflow: ellipsis;
	}
	${(p) => p.$highlight === 'red' && `
		background-color: #ffcccc;
		& > a { color: #8b0000; font-weight: 600; }
	`}
	${(p) => p.$highlight === 'green' && `
		background-color: #e8f5e9;
		& > a { color: #0d5c0d; font-weight: 600; }
	`}
	/* plain = no background (unchanged links) */
`;

const PhotoBlock = styled.div`
	display: flex;
	align-items: flex-start;
`;

export { ViewCandidateDiff };
